import React, { useState, useEffect } from 'react';
import { Search, FileText, Filter, ChevronDown, X } from 'lucide-react';
import { SelectedContract } from '../types';

interface ContractSelectorProps {
  selectedContract: SelectedContract | null;
  onSelectContract: (contract: SelectedContract) => void;
}

const STATUS_COLORS: Record<string, string> = {
  'active': 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  'draft': 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300',
  'pending_review': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  'under_review': 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  'expired': 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  'terminated': 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
};

export const ContractSelector: React.FC<ContractSelectorProps> = ({
  selectedContract,
  onSelectContract
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [contracts, setContracts] = useState<SelectedContract[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);

  // Fetch contracts from API
  useEffect(() => {
    const fetchContracts = async () => {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setIsLoading(false);
        return;
      }

      try {
        const apiUrl = process.env.REACT_APP_API_URL || process.env.REACT_APP_BACKEND_URL;
        const response = await fetch(
          `${apiUrl}/api/contracts?limit=100`,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        const data = await response.json();

        if (data.success && data.data) {
          setContracts(data.data.map((c: any) => ({
            id: c._id || c.id,
            _id: c._id || c.id,
            title: c.contractTitle || c.title || 'Untitled Contract',
            type: c.contractType || c.type || 'General',
            status: c.status || 'draft',
            supplierName: c.supplier?.companyName || c.supplierName || 'Unknown Supplier',
            startDate: c.startDate ? c.startDate.split('T')[0] : 'N/A',
            endDate: c.endDate ? c.endDate.split('T')[0] : 'N/A',
            jurisdiction: c.jurisdiction,
            riskFlags: c.riskFlags || [],
            value: c.value
          })));
        }
      } catch (error) {
        console.error('Failed to fetch contracts:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchContracts();
  }, []);

  const filteredContracts = contracts.filter(c => {
    const matchesSearch = c.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.supplierName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !statusFilter || c.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const uniqueStatuses = Array.from(new Set(contracts.map(c => c.status)));

  return (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b border-gray-200 dark:border-dark-border">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Contracts</h2>

        {/* Search */}
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search contracts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>

        {/* Filter toggle */}
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
        >
          <Filter className="w-4 h-4" />
          <span>Filters</span>
          <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
        </button>

        {/* Filters dropdown */}
        {showFilters && (
          <div className="mt-2 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
              Status
            </label>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setStatusFilter(null)}
                className={`px-2 py-1 text-xs rounded-full transition-colors ${
                  !statusFilter
                    ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300'
                    : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                All
              </button>
              {uniqueStatuses.map(status => (
                <button
                  key={status}
                  onClick={() => setStatusFilter(status === statusFilter ? null : status)}
                  className={`px-2 py-1 text-xs rounded-full capitalize transition-colors ${
                    status === statusFilter
                      ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300'
                      : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {status.replace('_', ' ')}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Contract List */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="space-y-2">
          {isLoading ? (
            // Loading skeletons
            Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="p-3 rounded-lg border border-gray-200 dark:border-gray-700 animate-pulse">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-gray-200 dark:bg-gray-700" />
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2" />
                    <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
                  </div>
                </div>
              </div>
            ))
          ) : filteredContracts.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p className="font-medium">
                {searchTerm || statusFilter ? 'No contracts found' : 'No contracts available'}
              </p>
              <p className="text-sm mt-1">
                {searchTerm || statusFilter ? 'Try adjusting your filters' : 'Upload a contract to get started'}
              </p>
            </div>
          ) : (
            filteredContracts.map((contract) => (
              <button
                key={contract.id}
                onClick={() => onSelectContract(contract)}
                className={`w-full text-left p-3 rounded-lg border transition-all ${
                  selectedContract?.id === contract.id
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 shadow-sm'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${
                    selectedContract?.id === contract.id
                      ? 'bg-primary-100 dark:bg-primary-900/50'
                      : 'bg-gray-100 dark:bg-gray-800'
                  }`}>
                    <FileText className={`w-4 h-4 ${
                      selectedContract?.id === contract.id
                        ? 'text-primary-600 dark:text-primary-400'
                        : 'text-gray-500 dark:text-gray-400'
                    }`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`font-medium truncate ${
                      selectedContract?.id === contract.id
                        ? 'text-primary-700 dark:text-primary-300'
                        : 'text-gray-900 dark:text-white'
                    }`}>
                      {contract.title}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                      {contract.supplierName}
                    </p>
                    <div className="flex items-center gap-2 mt-1.5">
                      <span className={`inline-block px-2 py-0.5 text-xs rounded-full capitalize ${
                        STATUS_COLORS[contract.status] || STATUS_COLORS['draft']
                      }`}>
                        {contract.status.replace('_', ' ')}
                      </span>
                      {contract.riskFlags && contract.riskFlags.length > 0 && (
                        <span className="text-xs text-amber-600 dark:text-amber-400">
                          {contract.riskFlags.length} risk{contract.riskFlags.length > 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      </div>

      {/* Selected count */}
      {filteredContracts.length > 0 && (
        <div className="p-3 border-t border-gray-200 dark:border-dark-border text-center">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {filteredContracts.length} contract{filteredContracts.length !== 1 ? 's' : ''} available
          </p>
        </div>
      )}
    </div>
  );
};

export default ContractSelector;
